import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AddPropertyPage } from './AddPropertyPage';
import type { PropertiesTab } from '../types/properties.types';
import { useDeleteArea, useDeleteProperty, useDeleteSector } from '@practium/features/api';
import { usePermissions } from '@practium/features/permissions';
import { useForBinding } from '@practium/features/forBinding';

export const AddPropertyPageClient = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { isForBinding } = useForBinding();
	const { abilities } = usePermissions();
	const type = (searchParams.get('type') as PropertiesTab) || 'properties';
	const propId = searchParams.get('propId') || '';
	const id = searchParams.get('id') || '';
	const areaId = searchParams.get('areaId') || '';
	const [createdId, setCreatedId] = useState('');
	const targetId = id || createdId;

	useEffect(() => {
		setCreatedId('');
	}, [type, propId]);

	const subject = isForBinding ? 'farmers-props' : 'props';
	const canDeleteAbility = abilities.can('delete', subject);
	const canEditAbility = abilities.can('update', subject);
	const canCreateAbility = abilities.can('create', subject);

	const deleteProperty = useDeleteProperty();
	const deleteArea = useDeleteArea();
	const deleteSector = useDeleteSector();

	const handleBack = () => {
		if (type === 'properties') {
			navigate('/propriedades');
			return;
		}
		navigate(`/propriedades?id=${propId}&tab=${type}`);
	};

	const handleGoToList = () => navigate('/propriedades');

	const handleCreateNext = (nextType: 'areas' | 'sectors', nextPropId: string) => {
		navigate(`/propriedades/novo?type=${nextType}&propId=${nextPropId}`);
	};

	const handleDelete = async () => {
		if (!targetId) return;
		if (type === 'properties') {
			await deleteProperty.mutateAsync(targetId);
			navigate('/propriedades');
			return;
		}
		if (type === 'areas') {
			await deleteArea.mutateAsync({ propId, areaId: targetId });
			navigate(`/propriedades?id=${propId}&tab=areas`);
			return;
		}
		await deleteSector.mutateAsync({ propId, areaId, sectorId: targetId });
		navigate(`/propriedades?id=${propId}&tab=sectors`);
	};

	return (
		<AddPropertyPage
			key={`${type}-${propId}`}
			type={type}
			propId={propId}
			onBack={handleBack}
			onGoToList={handleGoToList}
			onCreateNext={handleCreateNext}
			onCreated={setCreatedId}
			onDelete={targetId ? handleDelete : undefined}
			canDelete={canDeleteAbility}
			canEdit={canEditAbility}
			canCreate={canCreateAbility}
		/>
	);
};
